<template>
  <CsNavbar
    id="app-side-nav"
    ref="navbar"
    title="Menu"
    header-icon="mdi-menu"
    :class="{ 'app-side-nav--primary-disabled': !canCreateExcursion }"
    :primary-action-label="primaryActionLabel"
    :menu-items="menuItems"
    :footer-left-label="footerLeftLabel"
    @primary-action-click="createExcursion"
    @menu-item-click="handleMenuItemClick"
    @footer-left-click="viewUserInfo"
  />
  <!--
    CSDS gap workaround (see CSSES-977 AC3): CsNavbar wires HxTooltips to both
    the primary action and the footer-left button, but HxButton's render block
    drops v-tooltip's activator props (notably `ref`) on the floor, so the
    built-in tooltips never anchor. Menu items work because they use VListItem
    which forwards activator props correctly. Until CSDS fixes HxButton attrs
    forwarding, we mount our own v-tooltips here, anchored to the buttons via
    CSS selectors, only when the navbar is collapsed (cs-navbar--collapsed is
    added by CSDS on the root). open-delay matches HxTooltip's 500ms.
  -->
  <v-tooltip
    activator="#app-side-nav.cs-navbar--collapsed .cs-navbar__actions .v-btn"
    location="right"
    :open-delay="500"
    :close-delay="200"
    content-class="stb-tooltip pinch-left"
  >
    {{ primaryActionLabel }}
  </v-tooltip>
  <v-tooltip
    activator="#app-side-nav.cs-navbar--collapsed .cs-navbar__footer-left .v-btn"
    location="right"
    :open-delay="500"
    :close-delay="200"
    content-class="stb-tooltip pinch-left"
  >
    {{ footerLeftLabel }}
  </v-tooltip>
</template>

<script>
import { CsNavbar } from '@complispace/cs-design-system';

const allowed = (predicate, ctx) => (predicate ? predicate(ctx) : true);
const flag = (ctx, key) => ctx.$store.state.common[key];
const SIDEBAR_COLLAPSED = 'SideNavState';

const readCollapsed = () => {
  try {
    const saved = localStorage.getItem(SIDEBAR_COLLAPSED);
    console.log('saved', saved);
    return saved === null ? true : saved === 'true';
  } catch {
    return true;
  }
};

const writeCollapsed = (value) => {
  try {
    localStorage.setItem(SIDEBAR_COLLAPSED, value ? 'true' : 'false');
  } catch (e) {
    console.error('Local Storage is not found', e.message);
  }
};

const NAV_GROUPS = [
  {
    id: 'excursion-management',
    title: 'Excursion Management',
    icon: 'mdi-clipboard-text-outline',
    visibleWhen: (ctx) => !ctx.isAdminPanel,
    items: [
      { title: 'Excursions', link: '/excursions' },
      { title: 'Excursion Templates', link: '/excursions/templates' }
    ]
  },
  {
    id: 'system-admin',
    title: 'System Administration',
    icon: 'mdi-account-search-outline',
    visibleWhen: (ctx) => ctx.isAdminPanel,
    items: [
      {
        title: 'All Clients',
        link: '/admin/clients',
        visibleWhen: (ctx) => flag(ctx, 'clientsTabDisplayed')
      },
      {
        title: 'All Users',
        link: '/admin/users',
        visibleWhen: (ctx) => flag(ctx, 'usersTabDisplayed')
      }
    ]
  },
  {
    id: 'client-management',
    title: 'Client Management',
    icon: 'mdi-account-cog-outline',
    visibleWhen: (ctx) => ctx.isAdminPanel,
    items: [
      {
        title: 'Site Users',
        link: '/admin/clients/users',
        visibleWhen: (ctx) => flag(ctx, 'clientUsersTabDisplayed')
      },
      {
        title: 'Groups',
        link: '/admin/group',
        visibleWhen: (ctx) => flag(ctx, 'studentManagementSubjectManagementDisplayed')
      },
      {
        title: 'Student Management',
        link: '/admin/student-management',
        visibleWhen: (ctx) => flag(ctx, 'clientSelfGroupManagementDisplayed')
      },
      {
        title: 'Risk Matrix Management',
        link: '/admin/risk-management',
        visibleWhen: (ctx) => !flag(ctx, 'publicSchoolFeatureSetEnabled')
      },
      {
        title: 'Risk Category Management',
        link: '/admin/risk-category-management',
        visibleWhen: (ctx) => flag(ctx, 'riskCategoryManagementMenuItemDisplayed')
      },
      {
        title: 'Operation Management',
        link: '/admin/operation-management',
        visibleWhen: (ctx) => flag(ctx, 'operationManagementMenuItemDisplayed')
      }
    ]
  }
];

export default {
  name: 'AppSideNav',
  components: { CsNavbar },
  emits: ['view-user-info'],
  computed: {
    isAdminPanel() {
      return this.$route.path.includes('/admin');
    },
    canCreateExcursion() {
      const auth = this.$store.state.authorisation;
      const initInProgress = auth && auth.isInitClientConfigInProgress;
      return !initInProgress && flag(this, 'excursionCreationButtonDisplayed');
    },
    primaryActionLabel() {
      return 'Create Excursion';
    },
    footerLeftLabel() {
      return 'View User Information';
    },
    menuItems() {
      return NAV_GROUPS.filter((group) => allowed(group.visibleWhen, this))
        .map((group) => ({
          id: group.id,
          title: group.title,
          icon: group.icon,
          children: group.items
            .filter((item) => allowed(item.visibleWhen, this))
            .map(({ title, link }) => ({ title, link }))
        }))
        .filter((group) => group.children.length > 0);
    }
  },
  watch: {
    menuItems: {
      immediate: true,
      handler(newItems, oldItems) {
        const previousIds = new Set((oldItems || []).map((g) => g.id));
        const currentlyVisible = new Set(newItems.map((g) => g.id));
        this.$nextTick(() => {
          const navbar = this.$refs.navbar;
          if (!navbar) return;
          const opened = new Set(
            (navbar.openedGroups || []).filter((id) => currentlyVisible.has(id))
          );
          newItems.forEach((g) => {
            if (!previousIds.has(g.id)) opened.add(g.id);
          });
          navbar.openedGroups = Array.from(opened);
        });
      }
    }
  },
  mounted() {
    const navbar = this.$refs.navbar;
    if (!navbar) return;

    navbar.collapsed = readCollapsed();
    this.$watch(
      () => this.$refs.navbar && this.$refs.navbar.collapsed,
      (value) => {
        if (typeof value === 'boolean') writeCollapsed(value);
      }
    );
  },
  methods: {
    createExcursion() {
      if (!this.canCreateExcursion) return;
      window.location.assign('/excursions/new');
    },
    async handleMenuItemClick(item) {
      if (item?.link) {
        await this.navigateAndPreserveGroups(item.link, this.parentGroupIdForLink(item.link));
      } else {
        this.preserveOpenedGroups();
      }
    },
    viewUserInfo() {
      this.$emit('view-user-info');
      this.preserveOpenedGroups();
    },
    async navigateAndPreserveGroups(path, ensureOpenGroupId = null) {
      const openedSnapshot = this.snapshotOpenedGroups();
      if (ensureOpenGroupId && !openedSnapshot.includes(ensureOpenGroupId)) {
        openedSnapshot.push(ensureOpenGroupId);
      }
      try {
        await this.$router.push(path);
      } finally {
        this.restoreOpenedGroups(openedSnapshot);
      }
    },
    parentGroupIdForLink(link) {
      const group = NAV_GROUPS.find((g) => g.items.some((i) => i.link === link));
      return group ? group.id : null;
    },
    snapshotOpenedGroups() {
      const navbar = this.$refs.navbar;
      return navbar && Array.isArray(navbar.openedGroups) ? [...navbar.openedGroups] : [];
    },
    restoreOpenedGroups(snapshot) {
      // Two-phase apply: $nextTick handles Vue's queue, requestAnimationFrame
      // beats any internal CsNavbar reset on the next paint frame.
      const apply = () => {
        const navbar = this.$refs.navbar;
        if (!navbar || !Array.isArray(navbar.openedGroups)) return;
        const current = navbar.openedGroups;
        const same =
          current.length === snapshot.length && current.every((id, i) => id === snapshot[i]);
        if (!same) navbar.openedGroups = [...snapshot];
      };
      this.$nextTick(apply);
      if (typeof requestAnimationFrame === 'function') requestAnimationFrame(apply);
    },
    preserveOpenedGroups() {
      this.restoreOpenedGroups(this.snapshotOpenedGroups());
    }
  }
};
</script>

<style scoped lang="scss">
.app-side-nav--primary-disabled {
  :deep(.cs-navbar__actions) {
    opacity: 0.45;
    pointer-events: none;
    cursor: not-allowed;
  }
}
</style>
